// Data for Avenir's illustrative (anonymized) case studies.
// Each savings figure, utilization count, and capacity estimate is an
// illustrative example. See the confidentiality note rendered on each page.

export const CASE_STUDIES = {
  maternity: {
    slug: 'maternity',
    navLabel: 'Maternity Spend Driver',
    clientType: 'Self-insured employer',
    titleLead: 'Identifying the driver behind',
    titleAccent: 'maternity spend',
    heroDesc:
      'A self-insured employer saw an elevated concentration of cesarean sections in its North Carolina population. The pattern was visible in claims, but the driver behind it was not. Avenir connected the claims to clinical risk and regional access to explain why.',
    overview: [
      'Maternity spend is often evaluated at the point where the cost appears in claims data: delivery, facility charges, surgical procedures, and neonatal care. That view is useful, but it can miss the factors that develop earlier in the member journey.',
      'A self-insured employer noticed an elevated concentration of cesarean sections within its North Carolina population. The pattern was visible in the claims data, but the driver behind the pattern was not.',
      'The employer had reviewed the issue through existing tools, but manually evaluating maternity patterns state by state, then connecting those patterns to clinical risk factors and regional access conditions, was too time-intensive to complete quickly. They came to Avenir to understand what was driving the cost.',
    ],
    objective: {
      intro:
        'The objective was to understand the clinical and regional context behind elevated cesarean section volume in one state population. The analysis needed to answer:',
      questions: [
        'Was the pattern isolated to surgical utilization, or did it reflect earlier maternity risk?',
        'Were there clinical factors increasing the likelihood of cesarean delivery?',
        'Were regional access barriers contributing to the pattern?',
        'What intervention would be most appropriate for the employer to consider?',
      ],
      purpose: 'The purpose was to move from a claims-level observation to a targeted maternity strategy.',
    },
    approach: [
      'Avenir combined clinical claims data with more than 50 social determinants of health (SDOH) factors, including regional access to care, food access, rurality, and other population-level conditions that can influence maternal health before delivery.',
      "The analysis identified a connection between the employer's cesarean section concentration and higher diabetes prevalence among expecting mothers in the North Carolina population. Avenir then connected that finding to external clinical evidence showing that diabetes is a well-documented comorbidity associated with increased cesarean section risk.",
      'The elevated diabetes prevalence also correlated with regional conditions in North Carolina, including limited access to care and limited access to fresh food. In certain rural areas, these access barriers can make it harder for expecting mothers to receive consistent prenatal monitoring, nutrition support, and early intervention.',
    ],
    impact: {
      cards: [
        { icon: 'dollar', value: '$117,600/yr', label: 'Modeled savings opportunity', note: 'Identified through analysis. Actual savings depend on program adoption, member engagement, vendor performance, and plan design.' },
        { icon: 'clock', value: '53 hours', label: 'Capacity recovered', note: 'Analyst time removed per employer analysis (about 0.025 FTE).' },
        { icon: 'shield', value: 'Illustrative', label: 'Result type', note: 'Anonymized workflow; verified client figures available under NDA.' },
      ],
      basisTitle: 'How the modeled $117,600 opportunity is built',
      basis: [
        { label: 'Annual births in the analyzed population', value: '120' },
        { label: 'Observed cesarean section rate', value: '42%' },
        { label: 'Benchmark cesarean section rate', value: '32%' },
        { label: 'Excess cesarean sections', value: '12' },
        { label: 'Estimated incremental cost per cesarean delivery', value: '$9,800' },
        { label: 'Gross annual claims opportunity', value: '$117,600' },
      ],
    },
    hours: {
      steps: [
        { label: 'State-level maternity claims review', value: '18 hours' },
        { label: 'Cesarean section benchmark comparison', value: '6 hours' },
        { label: 'Diabetes and maternity risk analysis', value: '8 hours' },
        { label: 'Social determinants of health review', value: '10 hours' },
        { label: 'Clinical evidence review', value: '4 hours' },
        { label: 'Recommendation development', value: '5 hours' },
        { label: 'Employer-ready summary creation', value: '4 hours' },
      ],
      total: '55 hours',
      avenir: 'about 2 hours',
      saved: '53 hours',
    },
    capacity: {
      single: [
        { value: '53', label: 'analyst hours' },
        { value: '6.6', label: 'full workdays' },
        { value: '0.025 FTE', label: 'annual capacity' },
      ],
      scaledIntro: 'For a consulting team running this type of maternity analysis across 20 employer clients, this would represent:',
      scaled: [
        { label: 'Hours recovered', value: '1,060 hours' },
        { label: 'Workdays recovered', value: '132.5 days' },
        { label: 'Full-time equivalent capacity', value: '0.51 FTE' },
      ],
      redirect: 'That recovered capacity can be redirected toward employer strategy, vendor management, renewal planning, and client relationship work.',
    },
    findings: [
      'Avenir reframed the cost driver. The cesarean section concentration did not appear to be only a surgical utilization issue. It reflected upstream maternity risk connected to diabetes prevalence and access barriers.',
      'This distinction matters because different explanations lead to different interventions. A surgical billing analysis might lead an employer to focus on facility contracting, delivery setting, or procedure management. Those levers may still matter, but they do not address the earlier clinical risk factors that can increase the likelihood of surgical delivery.',
      "Avenir's analysis pointed to an earlier intervention path: improve access to prenatal support for members who may have limited local care options or higher risk factors before delivery.",
    ],
    recommended: {
      intro:
        "Through Avenir's analysis, the employer was able to evaluate earlier prenatal support options, including a remote maternity care program designed to reach members with limited local access. The recommended action focused on helping the employer engage members before the highest-cost claims appeared. That included:",
      items: [
        'Earlier identification of maternity risk',
        'Improved access to prenatal care resources',
        'Support for members in regions with limited provider access',
        'A more targeted maternity strategy for the North Carolina population',
      ],
    },
    whyItMatters: [
      'Maternity analytics often underperform when they focus only on the event that created the claim. Delivery costs are visible, but the drivers behind those costs often begin months earlier.',
      'For self-insured employers, that creates a planning challenge. If the analysis stops at the procedure level, the employer may optimize around the wrong problem.',
      "Avenir's approach connects claims data with clinical evidence and regional context. This allows employers and consultants to identify not only where spend is concentrated, but also the underlying factors that explain why it is happening. In this case, the analysis helped shift the strategy from reacting to surgical claims to addressing prenatal access and diabetes-related maternity risk earlier in the member journey.",
    ],
    takeaway: {
      text: ['Avenir helped the employer move from a visible cost pattern to a clearer explanation of the underlying driver.'],
      banner: 'A quantified maternity opportunity of $117,600 in modeled annual claims savings, and a more actionable strategy focused on earlier support, better access, and interventions aligned with the real source of risk.',
    },
  },

  'complex-care': {
    slug: 'complex-care',
    navLabel: 'Complex Care & Oncology',
    clientType: 'Self-insured employer',
    titleLead: 'Balancing cost and quality in',
    titleAccent: 'complex care',
    heroDesc:
      'A self-insured employer in Greater Boston faced rising complex-care costs, driven mainly by oncology. Avenir separated where high-acuity academic care was justified from routine services that could safely move to lower-cost settings.',
    overview: [
      'Complex care management is one of the most difficult areas of benefits strategy because the highest-cost care is not always inappropriate care.',
      'For self-insured employers, the goal is not simply to reduce utilization. The goal is to understand where high-cost care is clinically justified, where lower-cost settings can deliver comparable outcomes, and how to make those distinctions with enough precision to support member health.',
      'A self-insured employer with a concentration of employees in the Greater Boston area observed rising complex-care costs, primarily driven by oncology cases. The employer needed a strategy that could reduce avoidable cost while preserving access to high-quality cancer care, and came to Avenir to evaluate the cost and quality tradeoffs across care settings.',
    ],
    objective: {
      intro:
        'The objective was to determine which oncology-related services should remain in high-acuity academic medical centers and which services could be safely redirected to lower-cost outpatient settings. The analysis was designed to answer:',
      questions: [
        'Which care categories required a center of excellence?',
        'Which services were routine enough to consider alternative settings?',
        'Where could the employer reduce cost without creating a clinical tradeoff?',
        'How should the recommendation be communicated to support both financial and clinical decision-making?',
      ],
      purpose: 'The purpose was to create a targeted care-setting strategy, not a broad reduction in oncology spend.',
    },
    approach: [
      "Avenir analyzed the employer's complex-care cost patterns in the context of regional provider quality and service type. Greater Boston is an unusually dense healthcare market, with nationally recognized academic medical centers and cancer centers.",
      "For active, complex cancer treatment, these centers can represent the appropriate setting of care. In genuinely complex cases, the clinical outcomes and specialized capabilities may justify the higher cost. Avenir's analysis separated those cases from routine, recurring services that often do not require the same setting.",
      'This distinction allowed Avenir to evaluate oncology spend across two categories: complex cancer treatment, where academic centers may be appropriate, and routine services such as infusion and imaging, where lower-cost outpatient settings may deliver comparable quality at reduced cost.',
    ],
    impact: {
      cards: [
        { icon: 'dollar', value: '$524,000/yr', label: 'Modeled savings opportunity', note: 'Reflects care-setting optimization. Actual savings depend on clinical appropriateness, provider availability, steerage strategy, member engagement, and plan design.' },
        { icon: 'clock', value: '54 hours', label: 'Capacity recovered', note: 'Analyst and consultant time removed per analysis (about 0.026 FTE).' },
        { icon: 'shield', value: 'Illustrative', label: 'Result type', note: 'Anonymized workflow; verified client figures available under NDA.' },
      ],
      basisTitle: 'How the modeled $524,000 opportunity is built',
      basis: [
        { label: 'Routine infusion visits per year', value: '220' },
        { label: 'Hospital outpatient cost per infusion', value: '$3,200' },
        { label: 'Lower-cost outpatient cost per infusion', value: '$1,350' },
        { label: 'Savings per redirected infusion', value: '$1,850' },
        { label: 'Modeled annual infusion savings', value: '$407,000' },
        { label: 'Oncology imaging visits per year', value: '180' },
        { label: 'Hospital outpatient imaging cost', value: '$1,200' },
        { label: 'Lower-cost outpatient imaging cost', value: '$550' },
        { label: 'Savings per redirected imaging visit', value: '$650' },
        { label: 'Modeled annual imaging savings', value: '$117,000' },
        { label: 'Total modeled annual opportunity', value: '$524,000' },
      ],
    },
    hours: {
      steps: [
        { label: 'Oncology claims segmentation', value: '12 hours' },
        { label: 'Identification of recurring routine services', value: '8 hours' },
        { label: 'Provider and facility cost comparison', value: '10 hours' },
        { label: 'Regional quality review', value: '8 hours' },
        { label: 'Site-of-care savings modeling', value: '8 hours' },
        { label: 'Clinical appropriateness review', value: '6 hours' },
        { label: 'Employer-ready recommendation development', value: '5 hours' },
      ],
      total: '57 hours',
      avenir: 'about 3 hours',
      saved: '54 hours',
    },
    capacity: {
      single: [
        { value: '54', label: 'analyst & consultant hours' },
        { value: '6.75', label: 'full workdays' },
        { value: '0.026 FTE', label: 'annual capacity' },
      ],
      scaledIntro: 'For a brokerage or consulting team running this analysis across 25 employer clients, this would represent:',
      scaled: [
        { label: 'Hours recovered', value: '1,350 hours' },
        { label: 'Workdays recovered', value: '168.75 days' },
        { label: 'Full-time equivalent capacity', value: '0.65 FTE' },
      ],
      redirect: 'That capacity can be redirected toward clinical strategy, employer consultation, renewal planning, and vendor negotiation.',
    },
    findings: [
      "Avenir found that the employer's oncology spend required a differentiated strategy. For active and complex oncology treatment, maintaining access to centers of excellence helped protect member outcomes. These cases often involve specialized care teams, advanced treatment protocols, and clinical complexity that justify the setting.",
      'For routine and ongoing services, the analysis showed an opportunity to shift care toward lower-cost outpatient settings without compromising quality.',
      'This created a more precise recommendation than a general site-of-care policy. The employer did not need to treat all oncology spend the same way. The analysis showed where high-cost care was appropriate and where recurring services could be managed more efficiently.',
    ],
    recommended: {
      intro: 'Avenir produced a tailored recommendation:',
      items: [
        'Retain complex oncology treatment at appropriate centers of excellence',
        'Direct routine infusion, imaging, and recurring services toward lower-cost outpatient settings when clinically appropriate',
        'Preserve access to specialized care for high-complexity cases',
        'Reduce avoidable spend in areas where lower-cost settings offer equivalent quality',
      ],
      outro: 'This approach allowed the employer to manage oncology costs without weakening the quality of care available to members.',
    },
    whyItMatters: [
      'Complex-care analytics often underperform when they focus only on total cost. High-cost claims can signal avoidable spend, but they can also signal clinically appropriate care for serious conditions.',
      'A cost-only view can create the wrong incentive. It may pressure employers to reduce spend in areas where quality, specialization, and outcomes matter most.',
      "Avenir's approach incorporates both cost and quality. That allows employers and consultants to make more defensible recommendations, especially in sensitive areas such as oncology. The result is a strategy that protects clinical value while identifying areas of avoidable cost.",
    ],
    takeaway: {
      text: ['Avenir helped the employer separate complex oncology treatment from routine oncology-related services.'],
      banner: 'That distinction produced a modeled $524,000 annual savings opportunity while preserving access to centers of excellence for high-complexity cancer care.',
    },
  },

  'glp-1': {
    slug: 'glp-1',
    navLabel: 'GLP-1 Strategy',
    clientType: 'National insurance brokerage',
    titleLead: 'From GLP-1 insight to action in',
    titleAccent: 'twenty minutes',
    heroDesc:
      'A national insurance brokerage used Avenir to move from GLP-1 spend analysis to action, evaluating alternatives, reviewing rebate structures, and launching a member campaign, in about twenty minutes.',
    overview: [
      'Glucagon-like peptide-1 (GLP-1) medications have become one of the fastest-growing pressures on employer pharmacy budgets.',
      'For self-insured employers and brokers, the challenge is not only identifying the spend. Most teams can see when GLP-1 utilization is increasing. The harder problem is acting quickly enough to manage cost, evaluate alternatives, understand rebate structures, and communicate member options.',
      'A national insurance brokerage used Avenir to move from GLP-1 spend analysis to action in approximately twenty minutes.',
    ],
    objective: {
      intro:
        'The objective was to evaluate GLP-1 spend and generate a practical response that could support employer decision-making and member communication. The work needed to answer:',
      questions: [
        'What lower-cost clinical alternatives may be available?',
        'How do Pharmacy Benefit Manager (PBM) rebate structures affect net cost?',
        'What should the employer communicate to members?',
        'Can the analysis move directly into action without requiring multiple disconnected workflows?',
      ],
      purpose: 'The purpose was to help the brokerage support its client with a faster, more complete GLP-1 strategy.',
    },
    approach: [
      'Avenir evaluated the GLP-1 spend pattern, identified lower-cost clinical alternatives, reviewed the relevant PBM rebate structure, and generated an employee awareness campaign.',
      'First, Avenir identified lower-cost clinical alternatives, including biosimilar and generic options where appropriate, focused on options that could maintain clinical efficacy while reducing avoidable cost. Second, Avenir mapped the rebate structure across the relevant PBM relationships, giving the employer visibility into net cost after rebates rather than list price.',
      'Third, Avenir generated and distributed an employee awareness campaign that directed members toward lower-cost weight-management options available through their benefits program. The full workflow was completed end to end in approximately twenty minutes.',
    ],
    impact: {
      cards: [
        { icon: 'dollar', value: '$739,200/yr', label: 'Modeled savings opportunity', note: 'Reflects pharmacy strategy, rebate review, and member outreach. Actual savings depend on clinical appropriateness, prescribing behavior, PBM contract terms, member engagement, and plan design.' },
        { icon: 'clock', value: '26h 40m', label: 'Capacity recovered', note: 'Team time removed on a single employer analysis (about 0.013 FTE).' },
        { icon: 'shield', value: 'Illustrative', label: 'Result type', note: 'Anonymized workflow; verified client figures available under NDA.' },
      ],
      basisTitle: 'How the modeled $739,200 opportunity is built',
      basis: [
        { label: 'Members using GLP-1 medications', value: '320' },
        { label: 'Average monthly gross cost per member', value: '$1,100' },
        { label: 'Annual gross GLP-1 spend', value: '$4,224,000' },
        { label: 'Members clinically appropriate for redirection', value: '35%' },
        { label: 'Members identified for redirection', value: '112' },
        { label: 'Estimated monthly savings per redirected member', value: '$550' },
        { label: 'Modeled monthly savings opportunity', value: '$61,600' },
        { label: 'Modeled annual savings opportunity', value: '$739,200' },
      ],
    },
    hours: {
      steps: [
        { label: 'GLP-1 utilization review', value: '4 hours' },
        { label: 'Cost trend analysis', value: '3 hours' },
        { label: 'PBM rebate structure review', value: '6 hours' },
        { label: 'Clinical alternative research', value: '5 hours' },
        { label: 'Employer recommendation development', value: '4 hours' },
        { label: 'Employee awareness campaign drafting', value: '3 hours' },
        { label: 'Internal review and formatting', value: '2 hours' },
      ],
      total: '27 hours',
      avenir: 'about 20 minutes',
      saved: '26 hours 40 minutes',
    },
    capacity: {
      single: [
        { value: '26.7', label: 'analyst, pharmacy & consultant hours' },
        { value: '3.3', label: 'full workdays' },
        { value: '0.013 FTE', label: 'annual capacity' },
      ],
      scaledIntro: 'For a brokerage managing GLP-1 analysis across 50 employer clients, this would represent:',
      scaled: [
        { label: 'Hours recovered', value: '1,335 hours' },
        { label: 'Workdays recovered', value: '166.9 days' },
        { label: 'Full-time equivalent capacity', value: '0.64 FTE' },
      ],
      redirect: 'That recovered capacity can be redirected toward client strategy, PBM negotiation, renewal planning, and high-value employer advisory work.',
    },
    findings: [
      'The GLP-1 opportunity was not limited to one lever. The employer needed a combined strategy that considered clinical alternatives, PBM economics, and member behavior.',
      'A list-price view would have been incomplete because rebates can materially change the net cost of pharmacy options. A claims-only view would also have been incomplete because spend management depends on what members know, what alternatives are available, and how quickly the employer can act.',
      'Avenir connected these pieces into one workflow. The brokerage was able to move from identifying the issue to producing a practical employer response in the same session.',
    ],
    recommended: {
      intro: 'Avenir supported three actions:',
      items: [
        'Evaluate lower-cost clinical alternatives where clinically appropriate',
        'Review PBM rebate dynamics to understand net cost',
        'Communicate available lower-cost weight-management options to members',
      ],
      outro: 'This allowed the brokerage to support the employer with both financial analysis and member-facing outreach.',
    },
    whyItMatters: [
      'Pharmacy analytics often underperform when they stop at spend identification. For fast-moving categories such as GLP-1 medications, identifying the trend is only the first step. Employers also need to understand the net-cost economics, evaluate alternatives, and communicate options to members quickly.',
      'When those steps happen across separate teams, spreadsheets, vendor reports, and communication workflows, the response can take weeks. During that time, utilization and cost continue to move.',
      "Avenir's approach connects analysis, recommendation, and action. For brokers and consultants, that means a GLP-1 conversation can move from observation to employer-ready response much faster.",
    ],
    takeaway: {
      text: ['Avenir helped a national brokerage complete GLP-1 analysis, rebate review, and member outreach in approximately twenty minutes.'],
      banner: 'The workflow identified a modeled $739,200 annual savings opportunity and recovered 26 hours and 40 minutes of team capacity on a single employer analysis.',
    },
  },
};

export const CASE_STUDY_NAV = [
  { to: '/case-studies', label: 'National Benefits Advisory Firm' },
  { to: '/case-studies/maternity', label: 'Maternity Spend Driver' },
  { to: '/case-studies/complex-care', label: 'Complex Care & Oncology' },
  { to: '/case-studies/glp-1', label: 'GLP-1 Strategy' },
];
